import {
  TextField,
  IconButton,
  Button,
  InputAdornment,
  Tooltip,
  CircularProgress,
  MenuItem,
  Divider,
  FormHelperText,
  FormControl,
  Box,
} from '@mui/material';
import { KeyboardArrowDown, Autorenew as RefreshIcon } from '@mui/icons-material';
import { obfuscateWithStars, CancelablePromise, formatDate } from '@localstack/services';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { ContentModal, CopyIconButton, Dropdown } from '@localstack/ui';

import { SubscriptionGroupEntry } from './types';
import {
  DialogContentRoot,
  DialogDropdownButton,
  DialogFieldLabel,
  DialogReadOnlyField,
  DialogSection,
  DialogTokenActions,
} from './styled';

/* ---------------------------------- types ---------------------------------- */

type SCIMSettingsDialogProps = {
  isOpen: boolean;
  idpName: string;
  baseUrl?: string;
  token?: string | null;
  subscriptionGroups?: SubscriptionGroupEntry[];
  isLoading?: boolean;
  isLoadingSubscriptions?: boolean;
  onClose: () => void;
  onTokenRotate: (idpName: string) => CancelablePromise<{
    id: string;
    org_id: string;
    idp_name: string;
    created: number;
    scim_token: string;
  }>;
};

/* --------------------------------- consts ---------------------------------- */

const DEFAULT_SUBSCRIPTION_LABEL = 'Subscription Name';
const DEFAULT_GROUP_LABEL = 'Group Name';

/* -------------------------------- component -------------------------------- */

export const SCIMSettingsDialog = ({
  isOpen,
  idpName,
  baseUrl,
  token,
  subscriptionGroups,
  isLoading,
  isLoadingSubscriptions,
  onClose,
  onTokenRotate,
}: SCIMSettingsDialogProps): ReactElement => {
  const items = subscriptionGroups ?? [];
  const hasSubscriptions = items.length > 0;

  const [selectedId, setSelectedId] = useState<string | null>(hasSubscriptions ? (items[0]?.id ?? null) : null);

  // Keep selection valid as items change
  useEffect(() => {
    if (!hasSubscriptions) {
      setSelectedId(null);
      return;
    }
    const stillExists = items.some((it) => it.id === selectedId);
    if (!stillExists) {
      setSelectedId(items[0]?.id ?? null);
    }
  }, [hasSubscriptions, items, selectedId]);

  /**
   * Builds human-friendly display labels for `items`.
   *
   * Rules:
   * - If a name is unique → "<name>"
   * - If a name appears multiple times → "<name> — YYYY-MM-DD"
   * - If also duplicated on the same date → "<name> — YYYY-MM-DD · <id>"
   *
   * @remarks
   * The returned array aligns 1:1 with `items`. When `hasSubscriptions` is falsy,
   * an empty array is returned.
   *
   * @returns {string[]} Display labels corresponding to `items`.
   */
  const displayLabels = useMemo(() => {
    if (!hasSubscriptions) return [];

    const nameCounts = new Map<string, number>();
    const nameDateCounts = new Map<string, number>();

    // First pass: count by name and by (name, date)
    for (const it of items) {
      const dateStr = formatDate(it.created);
      nameCounts.set(it.name, (nameCounts.get(it.name) ?? 0) + 1);
      const ndKey = `${it.name}|${dateStr}`;
      nameDateCounts.set(ndKey, (nameDateCounts.get(ndKey) ?? 0) + 1);
    }

    // Second pass: build labels with the rules above
    return items.map((it) => {
      const occurrencesForName = nameCounts.get(it.name) ?? 0;

      // Unique name — no date
      if (occurrencesForName <= 1) {
        return it.name;
      }

      // Duplicate name — include date; possibly id if same date duplicates exist
      const dateStr = formatDate(it.created);
      const base = `${it.name} — ${dateStr}`;
      const ndKey = `${it.name}|${dateStr}`;
      const occurrencesForNameDate = nameDateCounts.get(ndKey) ?? 0;

      return occurrencesForNameDate > 1 ? `${base} · ${it.id}` : base;
    });
  }, [items, hasSubscriptions]);

  const selectedIndex = hasSubscriptions && selectedId ? items.findIndex((it) => it.id === selectedId) : -1;

  const selectedLabel =
    hasSubscriptions && selectedIndex >= 0 ? displayLabels[selectedIndex] : DEFAULT_SUBSCRIPTION_LABEL;

  const groupValue = hasSubscriptions && selectedIndex >= 0 ? items[selectedIndex]?.group : DEFAULT_GROUP_LABEL;

  const [isEditingToken, setIsEditingToken] = useState(false);
  const [menuMinWidth, setMenuMinWidth] = useState<number | undefined>(undefined);

  const dropdownButtonRef = useRef<HTMLButtonElement>(null);

  const handleDropdownButtonClick = (_event: React.MouseEvent<Element>) => {
    if (dropdownButtonRef.current) {
      setMenuMinWidth(dropdownButtonRef.current.offsetWidth);
    }
  };

  const content = (
    <DialogContentRoot>
      {/* Identity Provider Name */}
      <DialogSection>
        <DialogFieldLabel variant="body1">Identity Provider Name</DialogFieldLabel>
        <DialogReadOnlyField
          fullWidth
          margin="dense"
          variant="outlined"
          size="medium"
          value={idpName}
          InputProps={{ readOnly: true }}
        />
      </DialogSection>

      {/* SCIM Base Connector URL */}
      <DialogSection>
        <DialogFieldLabel variant="body1">SCIM Base Connector URL</DialogFieldLabel>
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          size="medium"
          value={baseUrl}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <CopyIconButton
                  textToCopy={baseUrl || ''}
                  tooltipTitle="Copy SCIM Base Connector URL"
                  size="small"
                  iconFontSize="small"
                  disabled={!baseUrl}
                />
              </InputAdornment>
            ),
          }}
        />
      </DialogSection>

      {/* Bearer Auth Token */}
      <DialogSection sx={{ mb: 2 }}>
        <DialogFieldLabel variant="body1">Bearer Auth Token</DialogFieldLabel>
        <TextField
          disabled={isLoading}
          fullWidth
          margin="dense"
          variant="outlined"
          size="medium"
          value={obfuscateWithStars(token || '', 10)}
          helperText={
            <span>
              Regenerating your SCIM Bearer Auth Token will disable the current one. Be sure to update your identity
              provider <strong>{idpName}</strong> with the new token to avoid any interruptions.
            </span>
          }
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                {!isEditingToken ? (
                  <>
                    <Tooltip title="Regenerate Bearer Auth Token">
                      <span>
                        <IconButton disabled={isLoading} onClick={() => setIsEditingToken(true)} size="small">
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <CopyIconButton
                      textToCopy={token || ''}
                      tooltipTitle="Copy Bearer Auth Token"
                      size="small"
                      iconFontSize="small"
                      disabled={isLoading || !token}
                    />
                  </>
                ) : (
                  <DialogTokenActions>
                    {isLoading && <CircularProgress size={22} />}
                    <Button
                      disabled={isLoading}
                      size="small"
                      variant="outlined"
                      onClick={() => setIsEditingToken(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={isLoading}
                      color="primary"
                      size="small"
                      variant="contained"
                      onClick={async () => {
                        await onTokenRotate(idpName);
                        setIsEditingToken(false);
                      }}
                    >
                      Confirm
                    </Button>
                  </DialogTokenActions>
                )}
              </InputAdornment>
            ),
          }}
          sx={{ mb: 0 }}
        />
      </DialogSection>

      <Divider />

      {/* Subscriptions */}
      <DialogSection>
        <DialogFieldLabel variant="body1">Subscriptions</DialogFieldLabel>
        <FormControl fullWidth margin="dense">
          <Dropdown
            label={selectedLabel}
            menuListProps={{
              dense: true,
              sx: { minWidth: menuMinWidth, width: menuMinWidth, p: 0 },
            }}
            fullWidth
            renderButton={(props) => (
              <DialogDropdownButton
                {...props}
                ref={dropdownButtonRef}
                variant="outlined"
                endIcon={
                  isLoadingSubscriptions ? <CircularProgress size={18} color="inherit" /> : <KeyboardArrowDown />
                }
                onClick={(e) => {
                  if (!hasSubscriptions || isLoadingSubscriptions) return;
                  handleDropdownButtonClick(e);
                  props.onClick?.(e);
                }}
                disabled={!isLoadingSubscriptions && !hasSubscriptions}
                sx={{ cursor: isLoadingSubscriptions ? 'default' : undefined }}
                fullWidth
              >
                {isLoadingSubscriptions ? 'Loading subscriptions…' : props.label}
              </DialogDropdownButton>
            )}
          >
            {(closeDropdown) =>
              hasSubscriptions ? (
                items.map((entry, i) => (
                  <MenuItem
                    key={entry.id}
                    selected={selectedId === entry.id}
                    onClick={() => {
                      setSelectedId(entry.id);
                      closeDropdown();
                    }}
                  >
                    {displayLabels[i]}
                  </MenuItem>
                ))
              ) : (
                <MenuItem key={DEFAULT_SUBSCRIPTION_LABEL} selected disabled sx={{ width: '100%' }}>
                  {DEFAULT_SUBSCRIPTION_LABEL}
                </MenuItem>
              )
            }
          </Dropdown>
          <FormHelperText>
            {isLoadingSubscriptions || hasSubscriptions ? (
              <>
                Licenses are managed automatically as users are assigned or removed. Ensure the group name in your
                identity provider <strong>{idpName}</strong> matches the name below for successful syncing.
              </>
            ) : (
              'No active subscriptions available.'
            )}
          </FormHelperText>
        </FormControl>

        <Box mt={2} />

        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          size="medium"
          label="Group Name"
          value={groupValue}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <CopyIconButton
                  textToCopy={groupValue || ''}
                  tooltipTitle="Copy Group Name"
                  size="small"
                  iconFontSize="small"
                  disabled={!hasSubscriptions || !groupValue || groupValue === DEFAULT_GROUP_LABEL}
                />
              </InputAdornment>
            ),
          }}
        />
      </DialogSection>
    </DialogContentRoot>
  );

  return (
    <ContentModal
      open={isOpen}
      onClose={onClose}
      dialogTitle="SCIM Configuration"
      showCloseButton
      hideDialogActions
      modalMaxWidth="sm"
      fullWidth
      dialogContent={content}
    />
  );
};
